import { ROUTES } from '@/lib/routes';
import type { NavigationConfig } from '@/types/navigation';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import {
  Car,
  ClipboardList,
  Euro,
  FileText,
  Hash,
  Hotel,
  LayoutPanelLeft,
  Lock,
  Mail,
  School,
  SquareMenu,
  TicketPercent,
  User,
  UserCog,
  UserRoundSearch,
  Users,
  Zap,
} from 'lucide-react';

/**
 * Vérifie si l'utilisateur est superadmin
 */
function isSuperAdmin(user: SupabaseUser | null): boolean {
  return user?.app_metadata?.role === 'superadmin';
}

/**
 * Vérifie si l'utilisateur est admin ou superadmin
 */
function isAdminOrSuperAdmin(user: SupabaseUser | null): boolean {
  const role = user?.app_metadata?.role;
  return role === 'superadmin' || role === 'admin';
}

/**
 * Configuration centralisée de la navigation
 * Utilise les routes centralisées pour éviter la duplication
 */
const baseNavigationConfig: NavigationConfig = {
  sections: [
    {
      id: 'dashboard',
      title: 'Tableau de bord',
      items: [
        {
          id: 'tasks',
          label: 'Mes Tâches',
          icon: ClipboardList,
          href: ROUTES.DASHBOARD.TASKS,
        },
        {
          id: 'dashboard',
          label: 'Tableau de bord',
          icon: LayoutPanelLeft,
          href: ROUTES.DASHBOARD.HOME,
        },
      ],
    },
    {
      id: 'data',
      title: 'Data',
      items: [
        {
          id: 'forms',
          label: 'Formulaires',
          icon: FileText,
          href: ROUTES.DATA.FORMS.LIST,
        },
        {
          id: 'prospects',
          label: 'Demandes entrantes',
          icon: UserRoundSearch,
          href: ROUTES.DATA.PROSPECTS,
        },
        {
          id: 'teams',
          label: 'Équipages',
          icon: Users,
          href: ROUTES.DATA.CREWS.LIST,
        },
        {
          id: 'contacts',
          label: 'Contacts',
          icon: User,
          href: ROUTES.DATA.CONTACTS.LIST,
        },
        {
          id: 'vehicles',
          label: 'Véhicules',
          icon: Car,
          href: ROUTES.DATA.VEHICLES,
        },
        {
          id: 'products',
          label: 'Produits',
          icon: SquareMenu,
          href: ROUTES.DATA.PRODUCTS,
        },
        {
          id: 'schools',
          label: 'Écoles',
          icon: School,
          href: ROUTES.DATA.SCHOOLS,
        },
        {
          id: 'associations',
          label: 'Associations',
          icon: Hotel,
          href: ROUTES.DATA.ASSOCIATIONS,
        },
        {
          id: 'discounts',
          label: 'Réductions',
          icon: TicketPercent,
          href: ROUTES.DATA.DISCOUNTS,
        },
      ],
    },
    {
      id: 'admin',
      title: 'Admin',
      items: [
        {
          id: 'users',
          label: 'Utilisateurs & rôles',
          icon: UserCog,
          href: ROUTES.ADMIN.USERS,
        },
        {
          id: 'email-templates',
          label: "Modèles d'email",
          icon: Mail,
          href: ROUTES.ADMIN.EMAIL_TEMPLATES,
        },
        {
          id: 'bed-automations',
          label: 'Automatisations BED',
          icon: Zap,
          href: ROUTES.ADMIN.BED_AUTOMATIONS,
          // Ouverte aux admin : c'est le service administratif qui règle les
          // relances et clôture les éditions, pas seulement les superadmin.
          minRole: 'admin',
        },
        {
          id: 'reserved-crew-numbers',
          label: "Numéros d'équipage réservés",
          icon: Lock,
          href: ROUTES.ADMIN.RESERVED_CREW_NUMBERS,
        },
        {
          id: 'orphan-crew-numbers',
          label: "Numéros d'équipage orphelins",
          icon: Hash,
          href: ROUTES.ADMIN.ORPHAN_CREW_NUMBERS,
          // Accessible aux admins ET superadmins (la vue/RPC SQL tolèrent les
          // deux). Les autres items de la section restent superadmin-only.
          minRole: 'admin',
        },
        {
          id: 'pricing',
          label: 'Gestion des tarifs',
          icon: Euro,
          href: ROUTES.ADMIN.PRICING,
        },
      ],
    },
  ],
};

/**
 * Retourne la configuration de navigation filtrée selon le rôle de l'utilisateur
 */
export function getNavigationConfig(user: SupabaseUser | null): NavigationConfig {
  const sections = baseNavigationConfig.sections
    // Section Admin : masquée aux users simples. Pour les superadmin, tous les
    // items. Pour les admin (non superadmin), seuls les items minRole 'admin'.
    .filter((section) => {
      if (section.id === 'admin' && !isAdminOrSuperAdmin(user)) {
        return false;
      }
      return true;
    })
    .map((section) => {
      if (section.id === 'admin' && !isSuperAdmin(user)) {
        // Admin non superadmin : ne garder que les items explicitement ouverts
        // aux admins (minRole === 'admin').
        return {
          ...section,
          items: section.items.filter((item) => item.minRole === 'admin'),
        };
      }
      // Filtrer les items de la section Data pour les users (Asso 4L)
      if (section.id === 'data' && !isAdminOrSuperAdmin(user)) {
        // Pour les users (Asso 4L), masquer Réductions uniquement
        // Les formulaires sont accessibles en lecture seule
        return {
          ...section,
          items: section.items.filter((item) => item.id !== 'discounts'),
        };
      }
      return section;
    })
    // Une section admin vidée de tous ses items (cas improbable) ne s'affiche pas.
    .filter((section) => section.items.length > 0);

  return { sections };
}

/**
 * Configuration par défaut (pour compatibilité, sera dépréciée)
 * @deprecated Utiliser getNavigationConfig(user) à la place
 */
export const navigationConfig: NavigationConfig = baseNavigationConfig;
